<template>
  <form
    ref="formEl"
    class="form"
    :class="[
      { inline: sections.length === 1 && sections[0].fields.length < 3 },
      state,
    ]"
    @submit.prevent="!incompleteFields.length && submit()"
  >
    <section
      v-for="(section, index) in sections"
      :key="index"
      class="form-section"
    >
      <h4
        v-if="sections.length > 1"
        class="form-section-title"
        :style="{ animationDelay: delay + index * 0.25 + 's' }"
        v-text="section.title"
      />
      <template v-for="(field, i) in section.fields">
        <client-only
          v-if="field.type === 'element'"
          v-show="conditional(field)"
          :key="`${i}-el`"
        >
          <component
            :is="field.element"
            :class="field.class"
            :style="[
              field.style,
              { animationDelay: delay + index * 0.25 + i * 0.25 + 's' },
            ]"
            v-html="field.text"
          />
        </client-only>
        <LazyFormFieldStripe
          v-else-if="field.type === 'stripe'"
          v-show="conditional(field)"
          :key="`${i}-stripe`"
          v-bind="field"
        />
        <FormField
          v-show="conditional(field)"
          v-else
          :key="`${i}-field`"
          v-model="form[field.name]"
          v-bind="field"
          :style="{ animationDelay: delay + index * 0.25 + i * 0.25 + 's' }"
        />
      </template>
    </section>
    <div v-if="giftaidWarning" class="confirmation confirmation-warning">
      <p>Please consider opting in to Gift Aid</p>
    </div>
    <div v-if="incompleteFields.length" class="confirmation confirmation-error">
      <p>Please check you have filled out the following fields:</p>
      <ul v-if="incompleteFields.length">
        <li v-for="field in incompleteFields" :key="field" v-text="field" />
      </ul>
    </div>
    <div v-if="state === 'error'" class="confirmation confirmation-error">
      <p v-text="errorMessage" />
    </div>
    <AtomsLoader v-if="state === 'submitting' || state === 'getting-intent'" />
    <AtomsButton
      v-else-if="state !== 'submitted'"
      type="submit"
      class="form-submit"
      :event="`Form+Submit+-+${id}`"
      :appearance="['outline', 'reverse']"
      :disabled="incompleteFields.length"
      :icon="{ name: 'arrow-long-right' }"
      :text="state === 'submitted' ? '' : 'Send'"
      :style="delay && { animationDelay: delay + 's' }"
    />
    <div v-else class="form-success">
      <p><strong>Thank you!</strong></p>
    </div>
  </form>
</template>

<script lang="ts" setup>
import type { FormSection } from '~~/types'

const props = defineProps({
  id: String,
  delay: { type: Number, default: 0 },
  sections: {
    type: Array as () => FormSection[],
    default: () => [],
  },
})

const {
  public: { stripePublicKey },
} = useRuntimeConfig()

const {
  form,
  formEl,
  giftaidWarning,
  errorMessage,
  stripeEnabled,
  conditional,
  submission,
  formIsReady,
} = useForm(props)

const incompleteFields = ref([])

const onCompletion = async () => {
  try {
    await $fetch('/api/contact', {
      method: 'POST',
      body: form,
    })
    state.value = 'submitted'
  } catch (e: any) {
    state.value = 'error'
    errorMessage.value = ('message' in e ? e.message : e) as string
  }
}

const { state, submit, cardComplete } = useStripe(
  stripePublicKey,
  submission,
  errorMessage,
  onCompletion,
  stripeEnabled
)
watch(
  () => ({ ...form }),
  () => {
    incompleteFields.value = formIsReady(cardComplete)
  }
)
</script>

<style lang="scss">
.form {
  position: relative;
  margin: czn.$lg 0;
  width: calc(var(--character-width) * 0.75);
  max-width: 100%;

  &.submitted .form-section {
    opacity: 0.4;
    pointer-events: none;
  }

  .form-success {
    position: absolute;
    z-index: 100;
  }

  &.inline {
    display: flex;
    align-items: flex-end;

    > *:not(:last-child) {
      margin-right: czn.$md;
    }

    @include media('<=tablet') {
      width: 100%;
      flex-flow: row wrap;

      > * {
        margin: 0 0 czn.$md;
      }
    }
  }
  .confirmation {
    font-size: 0.9em;
    padding: czn.$md czn.$lg czn.$rg + czn.$sm;
    background: rgba(243, 202, 88, 0.3);
    margin-bottom: czn.$lg;
    border-radius: var(--br-items-lg);

    p:last-child,
    li:last-child {
      margin-bottom: 0;
    }

    &-error {
      background: rgba(crimson, 0.15);
    }
  }
  .form {
    &-section {
      width: 100%;
    }

    &-field {
      &:first-child:last-child {
        margin-bottom: 0;
      }
    }
    &-error {
      padding: czn.$md czn.$lg;
      background: rba(crimson, 0.2);
    }
    &-submit {
      align-self: unset;
    }
  }
}
</style>
